import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { buildEmployeeRecipientName } from "../../xml/employeeRecipientName";
import { Record3921 } from "./interface/3921-interface";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { formatDate } from "../../constants/formatDate";
import { parseAmount } from "../../constants/validationFunctions";

// Type definition to fix linter error for OtherThanTransferorGrp
type Form3921DetailItem = {
  "@_xmlns": string;
  "@_xmlns:xsd": string;
  TaxYr: string;
  RecordId: string;
  IssuerOfficeCd?: string;
  VoidInd: string;
  CorrectedInd: string;
  PrevSubmittedRecRecipientGrp?: any;
  RecipientDetail: {
    TIN: string;
    TINSubmittedTypeCd: string;
    PersonNameControlTxt?: string;
    PersonName?: {
      PersonFirstNm: string;
      PersonLastNm?: string;
    };
    BusinessNameControlTxt?: string;
    BusinessName?: {
      BusinessNameLine1Txt: string;
      BusinessNameLine2Txt?: string;
    };
    MailingAddressGrp: {
      USAddress: {
        AddressLine1Txt?: string;
        AddressLine2Txt?: string;
        CityNm?: string;
        StateAbbreviationCd?: string;
        ZIPCd?: string;
      };
    };
  };
  RecipientAccountNum?: string;
  StockOptionGrantedDt: string;
  StockOptionExercisedDt: string;
  ExercisePricePerShareAmt?: string;
  FMVPerShareOnExerciseDtAmt?: string;
  SharesTransferredCnt: string;
  OtherThanTransferorGrp?: {
    BusinessName: {
      BusinessNameLine1Txt: string;
      BusinessNameLine2Txt?: string;
    };
    MailingAddressGrp?: {
      USAddress: {
        AddressLine1Txt?: string;
        CityNm?: string;
        StateAbbreviationCd?: string;
        ZIPCd?: string;
      };
    };
    TIN?: string;
  };
};

export async function generate3921Xml(
  ctx: EngineContext,records: Record3921[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  // Initialize the XML builder.
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: false,
  });

  // Determine tax year from the first record.
  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);

  // Build an array of form details, one for each record.
  const form3921Details: Form3921DetailItem[] = records.map((record) => {
    // TS-7939: person vs business (estate/trust) recipient
    const recipientName = buildEmployeeRecipientName(record as any);

    // Map to XSD structure - follow exact order from schema
    return {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: taxYear,
      RecordId: (record.id).toString(),
      VoidInd: "0",
      CorrectedInd: record.corrected ? "1" : "0",
      // TY2026 layout-V2 records file via the shared split builder (business
      // as BusinessName+BUSINESS_TIN); legacy records keep the shape below.
      RecipientDetail:
        (buildSplitRecipientDetail(
          record as any,
          transmitterData.testFileIndicator === "T"
            ? "000000000"
            : record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
          {
            USAddress: {
              ...(record.EMPLOYEE_ADDRESS1 && {
                AddressLine1Txt: record.EMPLOYEE_ADDRESS1,
              }),
              ...(record.EMPLOYEE_ADDRESS2 && {
                AddressLine2Txt: record.EMPLOYEE_ADDRESS2,
              }),
              ...(record.EMPLOYEE_CITY && { CityNm: record.EMPLOYEE_CITY }),
              ...(record.EMPLOYEE_STATE && {
                StateAbbreviationCd: record.EMPLOYEE_STATE,
              }),
              ...(record.EMPLOYEE_ZIP && { ZIPCd: record.EMPLOYEE_ZIP }),
            },
          }
        ) as Form3921DetailItem["RecipientDetail"] | null) ?? {
        TIN:
          transmitterData.testFileIndicator === "T"
            ? "000000000"
            : record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
        ...recipientName,
        MailingAddressGrp: {
          USAddress: {
            ...(record.EMPLOYEE_ADDRESS1 && {
              AddressLine1Txt: record.EMPLOYEE_ADDRESS1,
            }),
            ...(record.EMPLOYEE_ADDRESS2 && {
              AddressLine2Txt: record.EMPLOYEE_ADDRESS2,
            }),
            ...(record.EMPLOYEE_CITY && { CityNm: record.EMPLOYEE_CITY }),
            ...(record.EMPLOYEE_STATE && {
              StateAbbreviationCd: record.EMPLOYEE_STATE,
            }),
            ...(record.EMPLOYEE_ZIP && { ZIPCd: record.EMPLOYEE_ZIP }),
          },
        },
      },
      ...(record.ACCOUNT_NUMBER && {
        RecipientAccountNum: record.ACCOUNT_NUMBER,
      }),
      // Required by schema: dates must be in YYYY-MM-DD format
      StockOptionGrantedDt: formatDate(record.BOX1_DATE_GRANTED || new Date()),
      StockOptionExercisedDt: formatDate(
        record.BOX2_DATE_EXERCISED || new Date()
      ),
      // Optional numeric fields - format with no decimal point
      ...(record.BOX3_EXE_PRICE_PER_SHARE && {
        ExercisePricePerShareAmt: parseAmount(record.BOX3_EXE_PRICE_PER_SHARE),
      }),
      ...(record.BOX4_FAIR_MARKET_VALUE_ON_SHARE && {
        FMVPerShareOnExerciseDtAmt: parseAmount(
          record.BOX4_FAIR_MARKET_VALUE_ON_SHARE
        ),
      }),
      // Required field - convert to string
      SharesTransferredCnt: String(
        parseInt(record.BOX5_SHARES_TRANSFERRED || "0", 10)
      ),
    };
  });

  // Add OtherThanTransferorGrp if needed - follow order from XSD
  form3921Details.forEach((detail, index) => {
    const record = records[index];
    if (
      record.BOX6_COMPANY ||
      record.BOX6_ADDRESS ||
      record.BOX_6D ||
      record.BOX6_EIN
    ) {
      // Required: BusinessName must be present and have at least BusinessNameLine1Txt
      detail.OtherThanTransferorGrp = {
        BusinessName: {
          BusinessNameLine1Txt:
            record.BOX6_COMPANY || "Corporation Name Required",
        },
        ...(record.BOX6_ADDRESS && {
          MailingAddressGrp: {
            USAddress: {
              ...(record.BOX6_ADDRESS && {
                AddressLine1Txt: record.BOX6_ADDRESS,
              }),
              // Since we don't have city/state/zip for BOX6, we'll use employee info as fallback
              ...(record.EMPLOYEE_CITY && {
                CityNm: record.EMPLOYEE_CITY,
              }),
              ...(record.EMPLOYEE_STATE && {
                StateAbbreviationCd: record.EMPLOYEE_STATE,
              }),
              ...(record.EMPLOYEE_ZIP && {
                ZIPCd: record.EMPLOYEE_ZIP,
              }),
            },
          },
        }),
        ...(record.BOX6_EIN && {
          TIN: record.BOX6_EIN.replace(/-/g, ""),
        }),
      };
    }
  });

  // Generate unique identifiers
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  // Build the complete envelope structure according to XSD
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "1",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: "1",
        TotalRecipientFormCnt: form3921Details.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "3921",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: "2023-04-04",
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: form3921Details.length.toString(),
            IRSubmission1FormTotals: {
              Form3921TotalAmtGrp: {
                // If no ExercisePricePerShareAmt values, provide 0 as default
                ExercisePricePerShareAmt: records
                  .reduce((sum, r) => {
                    const amount = parseFloat(
                      r.BOX3_EXE_PRICE_PER_SHARE || "0"
                    );
                    return sum + (isNaN(amount) ? 0 : amount);
                  }, 0)
                  .toFixed(2),
                // If no FMVPerShareOnExerciseDtAmt values, provide 0 as default
                FMVPerShareOnExerciseDtAmt: records
                  .reduce((sum, r) => {
                    const amount = parseFloat(
                      r.BOX4_FAIR_MARKET_VALUE_ON_SHARE || "0"
                    );
                    return sum + (isNaN(amount) ? 0 : amount);
                  }, 0)
                  .toFixed(2),
              },
            },
          },
          IRSubmission1Detail: {
            Form3921Detail: form3921Details,
          },
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return { xml, taxYear };
  } catch (error: any) {
    console.error("Error generating XML:", error);
    throw new Error(`Failed to generate XML: ${error.message}`);
  }
}
